<template>
    <loading-spinner v-if="loading" />
    <v-row v-else>
        <!-- LEFT SIDE -->
        <v-col cols="12" md="6">
            <div class="white-card">
                <back-button fixed />
                <v-row>
                    <!-- IMG -->
                    <v-col cols="12">
                        <v-img eager max-height="250" contain class="routine-img" :src="programImg" />
                    </v-col>
                    <!-- NAME -->
                    <v-col cols="12">
                        <div class="routine-name">
                            {{ routineName }}
                        </div>
                        <div class="routine-text">
                            {{ routineBlocksExercises }}
                        </div>
                        <div class="routine-text">
                            {{ routineDuration }}
                        </div>
                    </v-col>
                    <v-col cols="12" class="text-center">
                        <v-btn v-if="canEdit" class="normal-btn mr-2" @click="editRoutine" outlined color="#797979"
                            small>
                            {{ $t('routines.edit_routine') }}
                        </v-btn>
                    </v-col>
                    <!-- DIVIDER -->
                    <v-col cols="12">
                        <v-divider />
                    </v-col>
                    <!-- CHART -->
                    <v-col cols="12">
                        <div class="routine-text bolder">
                            {{ routineOf }}
                        </div>
                        <div class="routine-text">
                            {{ $t('common.made_of') }}
                        </div>
                        <pie-chart style="height: 200px" :labels="labels" :datasets="datasets" />
                    </v-col>
                </v-row>
            </div>
        </v-col>
        <!-- RIGHT SIDE -->
        <v-col cols="12" md="6">
            <div class="white-card">
                <!-- BLOCKS -->
                <routine-blocks readonly :defaultBlocks="blocks" />
            </div>
        </v-col>
    </v-row>
</template>

<script>

import RoutineBlocks from '@/components/coach/routines/RoutineBlocks'
import { db } from "@/utils/Firebase";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import BackButton from "@/components/common/BackButton";
import { clearBlocksAssignedRoutine, getMergedBlocks } from "@/utils/Exercises";
import PieChart from "@/components/common/PieChart";

export default {
    components: { BackButton, LoadingSpinner, RoutineBlocks, PieChart },
    data() {
        return {
            program: null,
            loading: false,
            datasets: [],
            labels: [],
            colors: ['#99CC00', '#797979', '#2C3149', '#078571', '#66CCCC'],
        }
    },
    computed: {
        programId(vm = this) {
            return vm.$route.params.uid || null
        },
        routineId(vm = this) {
            return vm.$route.params.id
        },
        isCompany(vm = this) {
            return vm.account?.accountType === 'company'
        },
        isOwner(vm = this) {
            return vm.program?.coachId === vm.currentUser?.uid
        },
        canEdit(vm = this) {
            return vm.isOwner || vm.isCompany
        },
        programImg(vm = this) {
            return vm.program?.img || vm.imgPlaceholder
        },
        routine(vm = this) {
            return vm.program?.assignedRoutines[vm.routineId] || null
        },
        routineName(vm = this) {
            return `${vm.$t('programs.routine_name')} ${Number(vm.routineId || 0) + 1}`
        },
        blocks(vm = this) {
            return clearBlocksAssignedRoutine(vm.routine?.blocks || [])
        },
        routineBlocksExercises(vm = this) {
            let qtyBlocks = vm.routine?.blocks?.length || 0
            let qtyExercises = 0
            vm.routine?.blocks?.forEach(block => qtyExercises += block.exercises?.filter(exercise => exercise.type === 'exercise')?.length || 0)
            return `${qtyBlocks} ${vm.$tc('common.blocks', qtyBlocks)} / ${qtyExercises} ${vm.$tc('exercises.exercises', qtyExercises)}`
        },
        routineOf(vm = this) {
            if (vm.routine?.description?.objective) return `${vm.$t('routines.routine_of')} ${vm.routine?.description?.objective}`
            return vm.$t('common.routine')
        },
        routineDuration(vm = this) {
            if (!vm.routine) return vm.$t('common.not_available_short')
            let minutes = 0

            vm.routine.blocks.forEach(block => {
                block.exercises.forEach(exercise => {
                    minutes += exercise.config && exercise.config.sets.length || 0
                })
            })

            let minutesString = vm.$tc(`common.minutes`, Number(minutes) === 1 ? 1 : 2).toLowerCase()
            let minutesAdded = Number(minutes * 1.3).toFixed()
            if (Number(minutes) === Number(minutesAdded)) return `${minutes} ${minutesString}`
            return `${minutes} - ${minutesAdded} ${minutesString}`
        },
    },
    async mounted() {
        const vm = this
        await vm.loadProgram()
    },
    methods: {
        async loadProgram() {
            const vm = this
            vm.loading = true
            let programRef = await db.collection('programs').doc(vm.programId).get()
            vm.program = programRef.data()
            vm.loading = false
        },
        async loadRoutineChart() {
            const vm = this
            if (!vm.routine) return;
            let data = []
            let labels = []

            const blocks = await getMergedBlocks(vm.blocks)

            blocks.forEach(block => {
                block.exercises.forEach(exercise => {
                    if (exercise.type === 'exercise' && exercise.categories) labels.push(...exercise.categories)
                })
            })
            vm.labels = [...new Set(labels)]

            blocks.forEach(block => {
                vm.labels.forEach((label, i) => {
                    if (!data[i]) data[i] = 0
                    data[i] += block.exercises.filter(exercise => exercise.categories?.includes(label)).length || 0
                })
            })

            vm.datasets = [{
                data: data,
                backgroundColor: vm.colors,
            }]
        },
        editRoutine() {
            const vm = this
            vm.$router.push({ name: `${vm.accountType}.routines.program.routine.edit`, params: { uid: vm.$route.params.uid } })
        },
    },
    watch: {
        routine() {
            const vm = this
            vm.loadRoutineChart()
        }
    }
}
</script>

<style scoped>
.routine-img {
    border-radius: 8px;
}

.routine-name {
    font-size: 30px;
    line-height: 38px;
    color: #03342C;
    font-weight: bold;
}

.routine-text {
    font-weight: 300;
    font-size: 16px;
    line-height: 20px;
    color: #797979;
}

.v-divider {
    border-color: #797979;
    border-radius: 4px;
}
</style>